/**
 * UI-shaped types for the Network Collectors surfaces.
 *
 * Deliberately **not** wire types. No backend exists for Collectors or Log
 * Sources yet, so a DTO written here would be a contract we invented rather than
 * one we were given. These describe only what the screens render — camelCase,
 * no nullable-everything defensiveness — and are expected to be replaced
 * wholesale when the real endpoints land.
 *
 * A **Collector** is BitLyft's own agent, installed on a machine inside the
 * tenant's infrastructure; a **Log Source** is an integration *hosted on* a
 * Collector and cannot exist without one. See CONTEXT.md for the domain terms.
 */

/**
 * Liveness of the **agent**, from its heartbeat.
 *
 * Distinct from {@link LogSourceStatus}, which is about the flow of *data*: a
 * Collector can be `connected` while every source on it is silent, and that
 * pair of facts is what an operator needs to tell "the box is down" from "the
 * firewall stopped talking to the box".
 */
export const COLLECTOR_STATUSES = ['connected', 'degraded', 'offline'] as const

export type CollectorStatus = (typeof COLLECTOR_STATUSES)[number]

/**
 * Flow of **data** through one Log Source.
 *
 * `silent` = nothing received in 24 hours (a fixed check that feeds health
 * notifications). `awaiting_first_logs` = registered, but nothing has arrived
 * yet — a new source, not a broken one.
 */
const LOG_SOURCE_STATUSES = [
	'receiving',
	'silent',
	'awaiting_first_logs',
] as const

export type LogSourceStatus = (typeof LOG_SOURCE_STATUSES)[number]

/**
 * Syslog transport. UDP/TCP only — internal traffic isn't TLS-wrapped, and the
 * hop that does leave the building is the Collector's own outbound HTTPS.
 *
 * An array rather than a bare union because the Add Log Source wizard's
 * transport control iterates it: a control built from the type's own vocabulary
 * cannot offer a transport a `LogSource` could not hold.
 */
export const LOG_SOURCE_TRANSPORTS = ['udp', 'tcp'] as const

export type LogSourceTransport = (typeof LOG_SOURCE_TRANSPORTS)[number]

export type LogSource = {
	id: string
	/** The Collector hosting it. A Log Source has no identity without one. */
	collectorId: string
	/**
	 * A label, not a stream name: several sources of the same *type* across
	 * different Collectors still land in one shared stream.
	 */
	name: string
	/** Log Source Type, as the catalog names it — `firewall`, `ngfw`, `nxlog`. */
	type: string
	transport: LogSourceTransport
	port: number
	status: LogSourceStatus
	/**
	 * When a log last arrived through this source, ISO 8601.
	 *
	 * `null` is a state rather than a missing value: a source that has never
	 * received anything is `awaiting_first_logs`, and the detail table prints an
	 * em dash for it rather than an age it would have to invent.
	 */
	lastLogAt: string | null
}

/**
 * One Log Source, with the identity of the Collector hosting it.
 *
 * The manage page leads with `type · transport/port · last log · collector`, so
 * the Collector's *name* is part of what that page renders — a page holding only
 * `collectorId` would have to fetch the whole Collector to print one word.
 * `collectorStatus` rides along for the tone dot beside that name: an operator
 * looking at a silent source needs to know in the same glance whether the agent
 * under it is even alive.
 */
export type LogSourceWithCollector = LogSource & {
	collectorName: string
	collectorStatus: CollectorStatus
}

/**
 * What one on-demand packet capture returned.
 *
 * Deliberately not a tail: capture reads the source's port for a fixed window
 * and hands back what it saw. `lines` being empty is a **result**, not a
 * failure — the port was listening and nothing arrived, which is the sender
 * misconfiguration this panel exists to expose.
 */
export type CaptureSnapshot = {
	/** ISO 8601 — when the capture window closed. */
	capturedAt: string
	/** How long the collector listened. */
	windowSeconds: number
	/** Raw lines as they arrived on the wire, in arrival order. */
	lines: string[]
}

/**
 * A diagnostics check's verdict.
 *
 * Two values, not three: every check either confirms something works or reports
 * that it could not. There is no `fail` because none of these checks can prove a
 * negative — "no packets during a 30s capture" is a warning about a quiet
 * source, not proof the device is broken.
 */
export type DiagnosticsCheckStatus = 'pass' | 'warn'

export type DiagnosticsCheck = {
	id: string
	/** What was checked — `Port listener`, `Device reachability`. */
	name: string
	status: DiagnosticsCheckStatus
	/** One line an operator can act on, e.g. `udp/514 bound and receiving`. */
	result: string
	/** The command that produced {@link rawOutput} — `ss -an`, `tcpdump`. */
	command: string
	/** Verbatim command output, revealed by expanding the check. */
	rawOutput: string
}

/**
 * One diagnostics run: every check, and when it ran.
 *
 * `ranAt` is part of the result rather than component state because the panel's
 * whole claim is "this is what the collector saw *at that moment*" — a timestamp
 * kept beside the results cannot drift from them.
 */
export type DiagnosticsRun = {
	/** ISO 8601. */
	ranAt: string
	checks: DiagnosticsCheck[]
}

export type Collector = {
	id: string
	name: string
	/** Where the agent runs. The KPI strip counts *distinct* sites, not rows. */
	site: string
	status: CollectorStatus
	/** Last heartbeat, ISO 8601. */
	lastSeenAt: string
	/**
	 * The agent's address on the tenant's network.
	 *
	 * Shown with its own caveat on the detail page: devices point their syslog at
	 * this address, so changing it silently stops ingestion.
	 */
	ipAddress: string
	/** Agent build running on the host, e.g. `v1.4.2`. */
	agentVersion: string
	/**
	 * Volume this Collector shipped in the last 24h, **pre-formatted**.
	 *
	 * A string for the same reason `LogVolumeSummary` is one on the overview: no
	 * endpoint supplies this figure, so there is no unit to format from, and a
	 * byte ladder invented here would be a contract nobody agreed to.
	 */
	logVolume24h: string
}

/**
 * A Collector with the Log Sources hosted on it.
 *
 * The list renders both together (each row expands into its sources), so the
 * join is what the query returns rather than something every component redoes.
 */
export type CollectorWithSources = Collector & {
	sources: LogSource[]
}

/**
 * What AIR® made of one arriving record — the parsed half of a Verify tail line.
 *
 * A **wire value**, not a status: `allow` / `alert` / `deny` is what the device
 * itself wrote in the line above it, which is why it is spelled in the device's
 * own vocabulary rather than in either of this feature's status enums.
 */
export type VerifyRecordVerdict = 'allow' | 'alert' | 'deny'

/**
 * One record arriving on a newly registered listener, raw and parsed together.
 *
 * The pair is the point of the Verify step: the raw line proves the device is
 * sending, the parsed fields prove AIR® already understands it, and holding them
 * on one object is what stops the two panels showing different lines.
 */
export type VerifyRecord = {
	/** Sequence within this tail — see `buildVerifyRecord`. */
	id: string
	/** The line exactly as it arrived on the wire. */
	raw: string
	/** ISO 8601, stamped when the record arrived. */
	receivedAt: string
	/** The device that sent it, as the parser read it off the line. */
	host: string
	/** What kind of record it is — `traffic`, `threat/url`, `auth/failure`. */
	category: string
	verdict: VerifyRecordVerdict
}
